import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, m } from "motion/react";
import { Spinner } from "@vertex/ui/primitives/spinner";
import { useMovimento, useSentido } from "@vertex/ui/movimento";
import { ApiError } from "./lib/api";
import { pdvApi, type PdvContext } from "./lib/pdv";
import { useToast } from "./lib/toast";
import { ShortcutProvider } from "./keys/registry";
import { ShortcutsBar } from "./keys/shortcuts-bar";
import { ShortcutsHelp } from "./keys/shortcuts-help";
import { LoginScreen } from "./screens/login";
import { SaleScreen } from "./screens/sale";

/**
 * Caixa e recibo entram por `lazy()`: os dois usam o `Dialog`, que carrega
 * o motor de animação (Motion). Tirar isso do bundle inicial é a diferença entre
 * abrir o terminal em 1,2 s e em 2 s numa máquina velha — e o operador abre a
 * venda muito mais vezes do que abre o caixa.
 */
const RegisterScreen = lazy(() =>
  import("./screens/register").then((m) => ({ default: m.RegisterScreen })),
);

/**
 * Roteador do terminal: uma máquina de estados de três telas, não uma
 * biblioteca de rotas. Um kiosk não tem URL pra compartilhar, não tem botão
 * "voltar" do navegador (o Tauri esconde a barra) e não tem deep link —
 * `react-router` seria ~10 KB gz para resolver um problema que não existe aqui.
 */
type Screen = "sale" | "register";

export function App() {
  const notify = useToast();
  /* VOS Movimento: a venda é a "casa" do terminal. O caixa entra pela
     direita e a volta pra venda vem da esquerda (receita `vista`, com o
     sentido do índice da tela); a que sai deixa o fluxo na hora. */
  const mov = useMovimento();
  const [ctx, setCtx] = useState<PdvContext | null>(null);
  /** `null` = ainda verificando se há sessão viva; evita piscar o login. */
  const [booting, setBooting] = useState(true);
  const [screen, setScreen] = useState<Screen>("sale");
  const sentido = useSentido(screen === "register" ? 1 : 0);
  const [help, setHelp] = useState(false);
  /**
   * O wedge de código de barras vive no provider (um listener só, acima de
   * tudo), mas quem sabe adicionar ao carrinho é a tela de venda. Esta ref é a
   * ponte: a venda publica o "como", o provider dispara o "quando". Passar por
   * estado faria o listener remontar a cada item bipado.
   */
  const scan = useRef<((code: string, qty: number) => void) | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        setCtx(await pdvApi.context(signal));
      } catch (e) {
        if (signal?.aborted) return;
        /* 401 no boot é o caso NORMAL do terminal recém-aberto: cai no login
           sem alarde. Qualquer outro erro o operador precisa ver. */
        if (e instanceof ApiError && e.unauthenticated) setCtx(null);
        else notify((e as Error).message, "error");
      } finally {
        if (!signal?.aborted) setBooting(false);
      }
    },
    [notify],
  );

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const handleLogout = useCallback(async () => {
    try {
      await pdvApi.logout();
    } catch {
      /* Sessão já morta no servidor: o efeito local é o mesmo. */
    }
    setCtx(null);
    setScreen("sale");
  }, []);

  if (booting) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!ctx) return <LoginScreen onAuthenticated={() => void load()} />;

  return (
    /* O provider fica FORA do Suspense: o registry precisa existir antes da
       primeira tela, senão o rodapé de atalhos pisca a cada troca de tela. */
    <ShortcutProvider config={ctx.shortcuts} onScan={(code, qty) => scan.current?.(code, qty)}>
      <div className="flex h-full flex-col">
        {/* Posicionado e sem transbordo lateral: a tela que sai vira absoluta
            aqui dentro, e os 24px de lado da que entra não abrem barra. */}
        <div className="relative min-h-0 flex-1 overflow-x-hidden">
          <AnimatePresence initial={false} mode="popLayout" custom={sentido}>
            <m.div
              key={screen}
              className="h-full"
              custom={sentido}
              variants={mov.r.vista()}
              initial="entra"
              animate="fica"
              exit="sai"
            >
              {screen === "register" ? (
                /* O Suspense mora DENTRO da tela: suspender o caixa (o pedaço
                   dele ainda descendo) não pode esconder a presença inteira,
                   senão a venda que sai congela no meio da saída. */
                <Suspense
                  fallback={
                    <div className="flex h-full items-center justify-center">
                      <Spinner />
                    </div>
                  }
                >
                  <RegisterScreen ctx={ctx} onBack={() => setScreen("sale")} onChanged={() => void load()} />
                </Suspense>
              ) : (
                <SaleScreen
                  ctx={ctx}
                  onOpenRegister={() => setScreen("register")}
                  onLogout={() => void handleLogout()}
                  onSold={() => void load()}
                  onHelp={() => setHelp(true)}
                  registerScan={(fn) => {
                    scan.current = fn;
                  }}
                />
              )}
            </m.div>
          </AnimatePresence>
        </div>
        <ShortcutsBar />
      </div>
      {help ? <ShortcutsHelp onClose={() => setHelp(false)} /> : null}
    </ShortcutProvider>
  );
}
